import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { trackLead } from "@/lib/fbpixel";
import { trackCtaClick } from "@/lib/analytics/recordWebsiteAnalytics";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { Send, ArrowRight, ArrowLeft, CheckCircle2, Gift, Tag } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

type Step = "offer" | "contact" | "success";

const slideVariants = {
  enter: (dir: number) => ({ opacity: 0, x: dir > 0 ? 30 : -30 }),
  center: { opacity: 1, x: 0 },
  exit: (dir: number) => ({ opacity: 0, x: dir > 0 ? -30 : 30 }),
};

const OfferRedeemDialog = () => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>("offer");
  const [direction, setDirection] = useState(1);
  const [data, setData] = useState({
    discountCode: "",
    note: "",
    name: "",
    email: "",
    phone: "",
  });

  // Detekcia ?discount= alebo #kontakt?discount= v URL
  useEffect(() => {
    const detect = () => {
      try {
        const search = window.location.search || "";
        const hash = window.location.hash || "";
        const params = new URLSearchParams(search);
        const hashIdx = hash.indexOf("?");
        const hashParams = hashIdx >= 0 ? new URLSearchParams(hash.substring(hashIdx + 1)) : new URLSearchParams();
        const discount = (params.get("discount") || hashParams.get("discount") || "").toUpperCase();
        if (discount) {
          setData((prev) => ({ ...prev, discountCode: discount }));
          setOpen(true);
        }
      } catch (_) {
        // noop
      }
    };
    detect();
    window.addEventListener("hashchange", detect);
    const onOpenEvent = (e: Event) => {
      const detail = (e as CustomEvent<{ code?: string; note?: string }>).detail || {};
      const code = (detail.code || "").toUpperCase();
      setData((prev) => ({
        ...prev,
        discountCode: code || prev.discountCode,
        note: detail.note ?? prev.note,
      }));
      setStep("offer");
      setDirection(1);
      setOpen(true);
    };
    window.addEventListener("open-offer-redeem", onOpenEvent as EventListener);
    return () => {
      window.removeEventListener("hashchange", detect);
      window.removeEventListener("open-offer-redeem", onOpenEvent as EventListener);
    };
  }, []);

  const reset = () => {
    setStep("offer");
    setDirection(1);
    setData({ discountCode: "", note: "", name: "", email: "", phone: "" });
  };

  const handleOpenChange = (v: boolean) => {
    setOpen(v);
    if (!v) {
      // Vyčistíme URL aby sa pri ďalšom hashchange nezobrazil znova
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete("discount");
        if (url.hash.includes("discount=")) {
          const hash = url.hash.split("?")[0];
          url.hash = hash;
        }
        window.history.replaceState({}, "", url.toString());
      } catch {
        /* history API nedostupne — ignoruj */
      }
      reset();
    }
  };

  const goTo = (s: Step, dir: number) => {
    setDirection(dir);
    setStep(s);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!data.name.trim() || !data.email.trim()) {
      toast({ title: "Wypełnij imię i e-mail", variant: "destructive" });
      return;
    }
    const code = data.discountCode.trim().toUpperCase();
    const messageParts: string[] = [];
    if (code) messageParts.push(`🎁 Kod rabatowy: ${code}`);
    if (data.note.trim()) messageParts.push(data.note.trim());
    const message = messageParts.join("\n\n");

    try {
      const { error } = await supabase.functions.invoke("send-lead-email", {
        body: {
          name: data.name,
          email: data.email,
          phone: data.phone,
          message,
          type: "offer",
          language: "pl",
          source: "offer-email-cta",
        },
      });
      if (error) console.error("Lead email error:", error);
      else {
        trackLead({ source: "offer-email-cta", type: "offer", language: "pl", content_name: "Offer redeem" });
        trackCtaClick("offer-email-cta");
      }
    } catch (err) {
      console.error("Lead email failed:", err);
    }
    goTo("success", 1);
  };

  const stepIndex = step === "offer" ? 0 : step === "contact" ? 1 : 2;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md bg-card border-border">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            <span className="text-gradient">
              {step === "offer" && "Aktywuj zniżkę"}
              {step === "contact" && "Dane kontaktowe"}
              {step === "success" && "Dziękujemy!"}
            </span>
          </DialogTitle>
          {step !== "success" && (
            <div className="flex items-center gap-2 pt-2">
              {[0, 1].map((i) => (
                <div
                  key={i}
                  className={`h-1 flex-1 rounded-full transition-colors ${
                    i <= stepIndex ? "bg-primary" : "bg-primary/20"
                  }`}
                />
              ))}
            </div>
          )}
        </DialogHeader>

        <AnimatePresence mode="wait" custom={direction}>
          {step === "offer" && (
            <motion.div
              key="offer"
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.2 }}
              className="space-y-4 mt-4"
            >
              <div className="rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 border border-primary/20 p-4 flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <Gift className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-semibold text-sm text-foreground">Zniżka −10% z pierwszych 3 miesięcy</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Sprawdź kod i napisz nam, co Cię interesuje. Odpowiemy w ciągu 24 godzin.
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="or-code" className="flex items-center gap-2">
                  <Tag className="w-3.5 h-3.5 text-primary" />
                  Kod rabatowy
                </Label>
                <Input
                  id="or-code"
                  placeholder="np. WITAJ10"
                  value={data.discountCode}
                  onChange={(e) => setData({ ...data, discountCode: e.target.value.toUpperCase() })}
                  className="bg-background border-border font-mono tracking-wider uppercase"
                  maxLength={30}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="or-note">Uwagi — co Cię interesuje?</Label>
                <Textarea
                  id="or-note"
                  placeholder="Np. potrzebuję nowej strony dla kawiarni, interesuje mnie redesign istniejącej strony..."
                  value={data.note}
                  onChange={(e) => setData({ ...data, note: e.target.value })}
                  className="bg-background border-border min-h-[110px]"
                  maxLength={1000}
                />
              </div>

              <Button
                type="button"
                variant="gradient"
                className="w-full py-5 text-base"
                onClick={() => goTo("contact", 1)}
              >
                Dalej <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </motion.div>
          )}

          {step === "contact" && (
            <motion.form
              key="contact"
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.2 }}
              onSubmit={handleSubmit}
              className="space-y-4 mt-4"
            >
              <p className="text-sm text-muted-foreground">
                Ostatni krok — Twoje dane kontaktowe, na które wyślemy ofertę cenową ze zniżką.
              </p>

              {data.discountCode && (
                <div className="rounded-lg bg-primary/10 border border-primary/20 px-3 py-2 flex items-center gap-2 text-sm">
                  <Tag className="w-4 h-4 text-primary" />
                  <span className="text-muted-foreground">Aktywny kod:</span>
                  <span className="font-mono font-bold text-primary tracking-wider">{data.discountCode}</span>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="or-name">Imię i nazwisko *</Label>
                <Input
                  id="or-name"
                  placeholder="Jan Kowalski"
                  value={data.name}
                  onChange={(e) => setData({ ...data, name: e.target.value })}
                  className="bg-background border-border"
                  maxLength={100}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="or-email">Email *</Label>
                <Input
                  id="or-email"
                  type="email"
                  placeholder="jan@firma.pl"
                  value={data.email}
                  onChange={(e) => setData({ ...data, email: e.target.value })}
                  className="bg-background border-border"
                  maxLength={255}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="or-phone">Telefon</Label>
                <Input
                  id="or-phone"
                  type="tel"
                  placeholder="+48 XXX XXX XXX"
                  value={data.phone}
                  onChange={(e) => setData({ ...data, phone: e.target.value })}
                  className="bg-background border-border"
                  maxLength={20}
                />
              </div>

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => goTo("offer", -1)}
                  className="px-4 py-5"
                >
                  <ArrowLeft className="w-4 h-4" />
                </Button>
                <Button type="submit" variant="gradient" className="flex-1 py-5 text-base">
                  <Send className="w-4 h-4 mr-2" /> Wyślij zapytanie
                </Button>
              </div>
            </motion.form>
          )}

          {step === "success" && (
            <motion.div
              key="success"
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.2 }}
              className="flex flex-col items-center text-center py-8 space-y-4"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200, delay: 0.1 }}
              >
                <CheckCircle2 className="w-16 h-16 text-primary" />
              </motion.div>
              <h3 className="text-xl font-semibold text-foreground">Zapytanie wysłane pomyślnie!</h3>
              <p className="text-muted-foreground text-sm max-w-xs">
                Przygotujemy dla Ciebie ofertę cenową{data.discountCode ? ` ze zniżką ${data.discountCode}` : ""} i
                wyślemy ją na e-mail w ciągu 24 godzin.
              </p>
              <p className="text-muted-foreground text-xs">Odpowiedź otrzymasz na {data.email}</p>
              <Button
                variant="gradient"
                className="mt-4 px-8 py-5"
                onClick={() => handleOpenChange(false)}
              >
                Zamknij
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};

export default OfferRedeemDialog;
